import Image from 'next/image'
import image1 from './images/image1.jpg'
import image2 from './images/image2.jpg'
import image3 from './images/image3.jpg'
import image4 from './images/image4.jpg'
import { getProgramData } from '~shared/api/single-program-data/single-program-data'

// ─── Constants ────────────────────────────────────────────────────────────────

const DESCRIPTIONS = [
  'Дистанционное обучение. Можете обучаться из любой точки мира',
  'Возможность вернуть 13% от стоимости обучения',
  'Перевод из других ВУЗов без потери курса',
  'Образовательные программы, адаптированные под требования работодателей',
  'Поддержка куратора 24/7',
  'Программа трудоустройства после окончания обучения'
]

const COLORS = [
  '#DF6945',
  '#7D5EE4',
  '#F1AE2B',
  '#FB5449',
  '#4BAC89',
  '#ACBF2F'
]

// ─── TextCard ─────────────────────────────────────────────────────────────────

const TextCard = ({ text, bgColor }: { text: string; bgColor: string }) => (
  <div
    className='flex items-center p-[24px] 2lg:p-[32px] rounded-[16px] w-full h-full'
    style={{ backgroundColor: bgColor }}>
    <p className='text-[16px] md:text-[18px] 2lg:text-[20px] text-white font-normal leading-[130%]'>
      {text}
    </p>
  </div>
)

// ─── Component ────────────────────────────────────────────────────────────────

export const StudyIsSection = ({
  program
}: {
  program: Awaited<ReturnType<typeof getProgramData>>
}) => {
  const isHigherEducation = [
    'bakalavriat',
    'magistratura'
  ].includes(program?.category?.slug ?? '')

  const t0 = {
    text: program?.studyIs?.[0]?.name || DESCRIPTIONS[0],
    bgColor: COLORS[0]
  }
  const t1 = {
    text: program?.studyIs?.[1]?.name || DESCRIPTIONS[1],
    bgColor: COLORS[1]
  }
  const t2 = isHigherEducation
    ? {
        text: program?.studyIs?.[2]?.name || DESCRIPTIONS[2],
        bgColor: COLORS[2]
      }
    : null
  const t3 = {
    text: program?.studyIs?.[3]?.name || DESCRIPTIONS[3],
    bgColor: COLORS[3]
  }
  const t4 = {
    text: program?.studyIs?.[4]?.name || DESCRIPTIONS[4],
    bgColor: COLORS[4]
  }
  const t5 = isHigherEducation
    ? {
        text: program?.studyIs?.[5]?.name || DESCRIPTIONS[5],
        bgColor: COLORS[5]
      }
    : null

  const texts = [t0, t1, t2, t3, t4, t5].filter(Boolean) as {
    text: string
    bgColor: string
  }[]
  return (
    <section className='py-[64px] 2lg:py-[80px] bg-black'>
      <div className='mx-auto px-[16px] box-border max-w-[672px] md:max-w-[864px] 2lg:max-w-[1280px]'>
        {/* ─── Desktop ─── */}
        <div className='hidden 2lg:flex flex-col'>
          {/* Row 0: image1(104×104) + title */}
          <div className='flex gap-[32px] items-end mb-[8px]'>
            <div className='relative min-w-[175px] min-h-[175px] rounded-[16px] overflow-hidden ml-[20px]'>
              <Image
                src={image1}
                alt='image1'
                fill
                className='object-cover'
                quality={100}
              />
            </div>
            <h2 className='text-[48px] 2lg:text-[64px] font-medium text-white leading-[120%] text-center max-w-[850px] mb-[56px]'>
              Обучение в «Московском институте технологий и управления» — это
            </h2>
          </div>

          {/* Row 1: t0(384×136) · image2(180×180) · t1(384×136) */}
          <div className='flex items-center gap-[100px] h-[180px] justify-end'>
            <div className='w-[384px] h-[136px]'>
              <TextCard text={t0.text} bgColor={t0.bgColor} />
            </div>
            <div className='relative shrink-0 w-[104px] h-[104px] rounded-[16px] overflow-hidden'>
              <Image
                src={image2}
                alt='image2'
                fill
                className='object-cover'
                quality={100}
              />
            </div>
            <div className='shrink-0 w-[384px] h-[136px]'>
              <TextCard text={t1.text} bgColor={t1.bgColor} />
            </div>
          </div>

          {/* Row 2: spacer(104px) · t3(384×136) · image3(104×104) */}
          <div className='flex items-center gap-[100px] h-[180px] justify-end'>
            <div className='w-[384px] h-[136px]'>
              {t2 && <TextCard text={t2.text} bgColor={t2.bgColor} />}
            </div>
            <div className='w-[384px] h-[136px]'>
              <TextCard text={t3.text} bgColor={t3.bgColor} />
            </div>
            <div className='relative w-[180px] h-[180px] rounded-[16px] overflow-hidden'>
              <Image
                src={image3}
                alt='image3'
                fill
                className='object-cover'
                quality={100}
              />
            </div>
          </div>

          {/* Row 3: image4(104×104) · t4(384×136) · t5(384×136)? */}
          <div className='flex items-center gap-[100px] h-[180px] justify-start'>
            <div className='relative w-[104px] h-[104px] rounded-[16px] overflow-hidden ml-[50px]'>
              <Image
                src={image4}
                alt='image4'
                fill
                className='object-cover'
                quality={100}
              />
            </div>
            <div className='shrink-0 w-[384px] h-[136px]'>
              <TextCard text={t4.text} bgColor={t4.bgColor} />
            </div>
            <div className='shrink-0 w-[384px] h-[136px]'>
              {t5 && <TextCard text={t5.text} bgColor={t5.bgColor} />}
            </div>
          </div>
        </div>

        {/* ─── Mobile / Tablet ─── */}
        <div className='2lg:hidden flex flex-col gap-[40px]'>
          <h2 className='text-[32px] md:text-[40px] font-semibold text-white text-center mx-auto max-w-[83%]'>
            Обучение в «Московском институте технологий и управления» — это
          </h2>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-[32px]'>
            {texts.map((item, idx) => (
              <TextCard key={idx} text={item.text} bgColor={item.bgColor} />
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
